import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { dashboardApi } from '@/api/client';
import { LivePricesTable } from './LivePricesTable';
import { fmtFixed, fmtPct, intlLocale } from '@/lib/format';
import { withUsd } from '@/lib/utils';
import { AssetClassBadge } from '@/components/AssetType';
import type { AssetClass } from '@/lib/instruments';
import { safeHref } from "@/lib/safeHref";

/****************
 * Section two: the market.
 *
 * A BAND, not a panel. Guillaume: "I just don't want these tables to be too
 * much down and user to have to scroll a lot to reach them... they have to be
 * very well positioned and space efficient." So it is one row of six short
 * tiles over a single line of headlines, and nothing in it can grow: the tile
 * heights are fixed and the news strip shows one story at a time.
 *
 * Every figure here comes from someone else, and every tile says whose it is.
 * That is UI-6's actual requirement and not decoration — an unattributed
 * third-party index sitting beside our own metrics implies we computed it.
 *
 * A source that is merely OLD keeps showing its last real number with its age.
 * A source we have never reached says so. Neither can blank the row.
 ****************/

interface SourceMeta {
  source: string;
  as_of?: string;
  ok: boolean;
  stale: boolean;
}

interface MarketOverview {
  fear_greed: SourceMeta & { value: number; label: string; previous: number };
  caps: SourceMeta & {
    total: number; total2: number; total3: number;
    btc_dominance: number; eth_dominance: number;
    change_24h_pct: number; volume_24h: number;
  };
  macro: (SourceMeta & { symbol: string; name: string; price: number; change_24h_pct: number })[];
  book: SourceMeta & {
    symbol: string; mid: number; band_pct: number;
    bid_notional: number; ask_notional: number; bid_share_pct: number;
  };
  /* The live-price table's rows. Present in the response since the table was
     built and simply missing from this interface, so every read of it was an
     error the typecheck was not running to report. Shape taken from
     /api/v1/market/overview itself rather than guessed. */
  coins: SourceMeta & {
    rows: {
      symbol: string; name: string; price: number; change_24h_pct: number;
      market_cap: number; volume_24h: number; rank: number; image: string;
    }[];
  };
  predictions: SourceMeta & { markets: { question: string; yes_pct: number; slug: string; class?: NewsClass }[] };
  news: SourceMeta & {
    items: {
      title: string; link: string; source: string; published_at: string;
      class?: NewsClass; tone?: 'bullish' | 'bearish' | 'neutral'; held?: string;
    }[];
  };
  positioning?: Positioning;
  served_at: string;
}

/* A headline's or a market's type: the asset types, or macro (the Fed,
   yields, inflation), which moves all of them. market_news.go. */
type NewsClass = AssetClass | 'macro';

interface Positioning {
  ok: boolean;
  positions: number;
  classes: { class: AssetClass; long_value: number; short_value: number; long_count: number; short_count: number; traders: number }[];
  top_assets: { display: string; class: AssetClass; long_value: number; short_value: number; traders: number }[];
}

const compactUsd = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1e12) return withUsd(`${fmtFixed(v / 1e12, 2)}T`);
  if (a >= 1e9) return withUsd(`${fmtFixed(v / 1e9, 1)}B`);
  if (a >= 1e6) return withUsd(`${fmtFixed(v / 1e6, 1)}M`);
  if (a >= 1e3) return withUsd(`${fmtFixed(v / 1e3, 1)}k`);
  return withUsd(fmtFixed(v, 0));
};

const ago = (t: (key: string, opts?: Record<string, unknown>) => string, iso?: string) => {
  if (!iso) return t('market.ago.never');
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return t('market.ago.justNow');
  if (mins < 60) return t('market.ago.minutes', { n: mins });
  const h = Math.floor(mins / 60);
  if (h < 24) return t('market.ago.hours', { n: h });
  return t('market.ago.days', { n: Math.floor(h / 24) });
};

/*
 * One emoji per quarter of the scale — Guillaume asked for four, split at 25,
 * 50 and 75. Coarser than the index's own five bands, deliberately: a face is a
 * mood, and the exact number sits right beside it.
 */
const fgEmoji = (v: number) => (v < 25 ? '😱' : v < 50 ? '😟' : v < 75 ? '🙂' : '🤑');

/*
 * The plain-language reading of the two pressure signals.
 *
 * Bids outweighing asks is buyers leaning in; longs being the ones liquidated
 * means price fell into them. They agree more often than not, and when they
 * DISAGREE that is worth saying rather than averaging into a shrug — so the
 * label has four states, not three, and "Mixed" is a real answer.
 *
 * Deliberately blunt thresholds: 55/45 on the book, 60/40 on liquidations.
 * Anything tighter is noise dressed as a signal, and this is a one-word summary
 * with the exact percentages drawn underneath it.
 */
function pressureRead(bidShare: number, longLiqShare?: number): 'bullish' | 'bearish' | 'mixed' | 'flat' {
  const book = bidShare >= 55 ? 1 : bidShare <= 45 ? -1 : 0;
  // Longs being liquidated is BEARISH: price fell far enough to close them out.
  const liq = longLiqShare === undefined ? 0 : longLiqShare >= 60 ? -1 : longLiqShare <= 40 ? 1 : 0;
  if (book > 0 && liq >= 0) return 'bullish';
  if (book < 0 && liq <= 0) return 'bearish';
  if (book === 0 && liq > 0) return 'bullish';
  if (book === 0 && liq < 0) return 'bearish';
  if (book === 0 && liq === 0) return 'flat';
  return 'mixed';
}

/* Translation keys (dashboard namespace). */
const pressureLabel = (bid: number, longLiq?: number) => ({
  bullish: 'market.pressure.bullish',
  bearish: 'market.pressure.bearish',
  mixed: 'market.pressure.mixed',
  flat: 'market.pressure.flat',
}[pressureRead(bid, longLiq)]);

const pressureClass = (bid: number, longLiq?: number) => ({
  bullish: 'text-success',
  bearish: 'text-danger',
  mixed: 'text-warning',
  flat: 'text-muted-foreground',
}[pressureRead(bid, longLiq)]);

/** Fear and Greed's own scale, so the colour agrees with the word. */
const fgColor = (v: number) =>
  v <= 24 ? 'hsl(0, 70%, 55%)'
  : v <= 44 ? 'hsl(24, 80%, 55%)'
  : v <= 55 ? 'hsl(45, 85%, 55%)'
  : v <= 74 ? 'hsl(90, 55%, 48%)'
  : 'hsl(152, 60%, 45%)';

interface WhalePrint {
  at: string;
  exchange: string;
  symbol: string;
  buy: boolean;
  notional: number;
  price: number;
  quantity: number;
}

interface WhaleResponse {
  top: WhalePrint[];
  recent: WhalePrint[];
  buy_notional: number;
  sell_notional: number;
  total_notional: number;
  buy_share_pct: number;
  count: number;
  threshold: number;
  sources: string[];
  has_data: boolean;
  /** How much of the 24h window we actually hold. See whale_feed.go. */
  covered_hours: number;
  filling: boolean;
}

interface LiquidationBucket {
  bucket_start: string;
  exchange: string;
  long_notional: number;
  short_notional: number;
  event_count: number;
}

interface LiquidationsResponse {
  buckets: LiquidationBucket[];
  totals: { long_notional: number; short_notional: number; total_notional: number; event_count: number; long_share_pct: number };
  last_1h: { long_notional: number; short_notional: number; total_notional: number; event_count: number; long_share_pct: number };
  window_hours: number;
  sources: string[];
  has_data: boolean;
}

interface MarketBandProps {
  /** Symbol -> price from the websocket, for the rows it carries. */
  livePrices: Record<string, number>;
}

export function MarketBand({ livePrices }: MarketBandProps) {
  const { t } = useTranslation('dashboard');
  const { data, isLoading } = useQuery<MarketOverview>({
    queryKey: ['market', 'overview'],
    queryFn: () => dashboardApi.getMarketOverview(),
    // The backend refreshes on its own timers; this only re-reads its memory.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const { data: whales } = useQuery<WhaleResponse>({
    queryKey: ['market', 'whales', '24h'],
    queryFn: () => dashboardApi.getWhaleActivity('24h'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const { data: liq } = useQuery<LiquidationsResponse>({
    queryKey: ['market', 'liquidations', '24h'],
    queryFn: () => dashboardApi.getLiquidations('24h'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  return (
    /* Prices on the LEFT, market tiles to their right. Guillaume: "The Live
       price Chart should be at the left of the other Market data, so now the
       Top Traders Table would have the whole width of the page just for him."
       Moving it here is what frees the trader table, which is the point. */
    <div className="mb-8 grid grid-cols-1 xl:grid-cols-[300px_1fr] gap-4">
      {/* The Market section sets the row's height and the prices fill it
          (Guillaume, 2026-10-06: "the hight of the default table should match
          the hight of the market section next to it", which will grow). Out
          of the flow on wide screens so it cannot stretch the row itself. */}
      <div className="relative xl:min-h-[320px]">
        <div className="xl:absolute xl:inset-0">
          <LivePricesTable
            coins={data?.coins?.rows || []}
            caps={data?.caps}
            macro={data?.macro || []}
            livePrices={livePrices}
            loading={isLoading}
          />
        </div>
      </div>

      <div>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-base font-semibold text-foreground">{t('market.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('market.notOurs')}</p>
      </div>

      {/* Four tiles now, not seven: the caps, the dollar and gold moved into
          the price table where they belong beside a price. The ones left are
          the ones that are not prices, and they get the room he asked for. */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="glass-card rounded-xl p-3 h-[86px]">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-5 w-16 mt-2" />
            </div>
          ))
        ) : (
          <>
            <Tile label={t('market.fearGreed')} meta={data?.fear_greed} centred>
              {data?.fear_greed?.ok && (
                <div className="w-full">
                  <div className="flex items-baseline justify-center gap-1.5">
                    <span className="text-lg leading-none">{fgEmoji(data.fear_greed.value)}</span>
                    <span className="text-xl font-bold" style={{ color: fgColor(data.fear_greed.value) }}>
                      {data.fear_greed.value}
                    </span>
                    <span className="text-[11px] text-muted-foreground truncate">{data.fear_greed.label}</span>
                  </div>
                  <Bar pct={data.fear_greed.value} color={fgColor(data.fear_greed.value)} />
                </div>
              )}
            </Tile>

            <Tile
              label={t('market.dominance')}
              centred
              meta={data?.caps}
              info={t('market.dominance.info')}
            >
              {data?.caps?.ok && (
                <DominanceRing btc={data.caps.btc_dominance} eth={data.caps.eth_dominance} />
              )}
            </Tile>

            {/* Two readings of the same question in ONE square, because they
                answer it from opposite sides: the book is what is WAITING, the
                liquidations are what already got FORCED out. Guillaume asked for
                a plain-language reading on each and suggested combining them,
                which is right — apart they are two percentages, together they
                are a sentence. */}
            <Tile
              label={t('market.pressure')}
              meta={data?.book}
              info={t('market.pressure.info')}
            >
              {data?.book?.ok && (
                <>
                  <p className={`text-sm font-semibold ${pressureClass(data.book.bid_share_pct, liq?.totals.long_share_pct)}`}>
                    {t(pressureLabel(data.book.bid_share_pct, liq?.totals.long_share_pct))}
                  </p>

                  <div className="mt-1.5 space-y-1">
                    <MiniSplit
                      label={t('market.book')}
                      leftPct={data.book.bid_share_pct}
                      leftText={t('market.bids', { pct: fmtFixed(data.book.bid_share_pct, 0) })}
                    />
                    {liq?.has_data ? (
                      <MiniSplit
                        label={t('market.liq24h')}
                        leftPct={100 - liq.totals.long_share_pct}
                        leftText={t('market.longs', { pct: fmtFixed(liq.totals.long_share_pct, 0) })}
                        right={compactUsd(liq.totals.total_notional)}
                      />
                    ) : (
                      <p className="text-[10px] text-muted-foreground">{t('market.liqCollecting')}</p>
                    )}
                  </div>
                </>
              )}
            </Tile>

            <Tile
              label={t('market.whales')}
              meta={whales ? {
                source: (whales.sources || []).join(', ') || 'OKX, Bybit',
                ok: whales.has_data,
                stale: false,
              } : undefined}
              info={t('market.whales.info')}
            >
              {whales?.has_data && (
                <>
                  <p className="text-lg font-semibold">{compactUsd(whales.total_notional)}</p>
                  <div className="flex h-1.5 rounded overflow-hidden mt-1.5">
                    <div className="bg-success" style={{ width: `${whales.buy_share_pct}%` }} />
                    <div className="bg-danger flex-1" />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    <span className="text-success">{t('market.bought', { pct: fmtFixed(whales.buy_share_pct, 0) })}</span>
                    {' · '}
                    {t('market.prints', { n: whales.count.toLocaleString(intlLocale()) })}
                  </p>
                  {/* A rolling 24h that has not filled yet climbs, which reads
                      as an accumulating total unless it says otherwise. */}
                  {whales.filling && (
                    <p className="text-[10px] text-warning mt-0.5">
                      {whales.covered_hours < 1
                        ? t('market.soFarMin', { n: Math.round(whales.covered_hours * 60) })
                        : t('market.soFarH', { n: fmtFixed(whales.covered_hours, 1) })}
                    </p>
                  )}
                </>
              )}
              {whales && !whales.has_data && (
                <p className="text-[11px] text-muted-foreground mt-1">{t('market.collecting')}</p>
              )}
            </Tile>
          </>
        )}
      </div>

      {/* Stacked, not side by side. Guillaume: "For the predictions and Latest
          have them one on top of the other instead of next to each other." A
          question and a headline are both sentences, and columns of sentences
          are columns of truncation. */}
      <div className="space-y-3 mt-3">
        {data?.positioning?.ok && <PositioningRow p={data.positioning} />}

        <div className="glass-card rounded-xl p-3">
          <MetaLine label={t('market.predictions')} meta={data?.predictions} />
          {data?.predictions?.ok && data.predictions.markets.length > 0 ? (
            <div className="space-y-1 mt-1.5">
              {data.predictions.markets.slice(0, 4).map((m) => (
                <a
                  key={m.slug + m.question}
                  href={`https://polymarket.com/event/${m.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[11px] hover:text-foreground group"
                >
                  {m.class && <TypeBadge cls={m.class} />}
                  <span className="flex-1 text-muted-foreground group-hover:text-foreground line-clamp-2 leading-tight">
                    {m.question}
                  </span>
                  <span className="font-mono shrink-0">{fmtPct(m.yes_pct, 0)}</span>
                </a>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground mt-1.5">
              {data?.predictions?.ok ? t('market.noMarkets') : t('market.noPolymarket')}
            </p>
          )}
        </div>

        {/* The Biggest trades strip was here and is gone: the whale TILE above
            already carries the 24h number, and a second whale panel between the
            odds and the headlines separated two things that read together. */}
        <div className="glass-card rounded-xl p-3">
          <MetaLine label={t('market.latest')} meta={data?.news} info={t('market.tone.info')} />
          {data?.news?.ok && data.news.items.length > 0 ? (
            <div className="space-y-1 mt-1.5">
              {data.news.items.slice(0, 10).map((n) => (
                <a
                  key={n.link + n.title}
                  href={safeHref(n.link)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[11px] group"
                >
                  {n.class && <TypeBadge cls={n.class} />}
                  {n.tone && <ToneMark tone={n.tone} />}
                  {n.held && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-[9px] font-semibold text-primary shrink-0 cursor-help">{n.held}</span>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs max-w-[240px]">{t('market.news.held', { asset: n.held })}</TooltipContent>
                    </Tooltip>
                  )}
                  <span className="flex-1 truncate text-muted-foreground group-hover:text-foreground">{n.title}</span>
                  <span className="text-[10px] text-muted-foreground/70 shrink-0">{n.source}</span>
                  <ArrowSquareOut size={11} className="text-muted-foreground/50 shrink-0" />
                </a>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground mt-1.5">{t('market.noNews')}</p>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

/** A type badge, with Macro beside the three asset types. */
function TypeBadge({ cls }: { cls: NewsClass }) {
  const { t } = useTranslation('dashboard');
  if (cls === 'macro') {
    return (
      <span className="inline-flex items-center rounded border border-slate-400/40 text-slate-300 px-1 py-px text-[9px] font-medium uppercase leading-none tracking-wide shrink-0">
        {t('market.class.macro')}
      </span>
    );
  }
  return <AssetClassBadge cls={cls} className="shrink-0" />;
}

/** Bullish, bearish or neutral, as the reader reads the headline (market_news.go). */
function ToneMark({ tone }: { tone: 'bullish' | 'bearish' | 'neutral' }) {
  const { t } = useTranslation('dashboard');
  const look = tone === 'bullish' ? 'text-success' : tone === 'bearish' ? 'text-danger' : 'text-muted-foreground/60';
  const mark = tone === 'bullish' ? '▲' : tone === 'bearish' ? '▼' : '•';
  return (
    <span className={`text-[9px] font-semibold uppercase shrink-0 w-[60px] ${look}`}>
      {mark} {t(`market.tone.${tone}`)}
    </span>
  );
}

/****************
 * Where the traders we follow are positioned, by type. 2026-10-06.
 *
 * Guillaume agreed to the multi-asset Market section "but don't make it too
 * big". So one line: per type, the share of the money that is long, and the
 * three assets holding the most. Every trader on the roster, every public
 * venue, open positions only (market_positioning.go).
 ****************/
function PositioningRow({ p }: { p: Positioning }) {
  const { t } = useTranslation('dashboard');
  const shown = p.classes.filter((c) => c.class !== 'forex');
  return (
    <div className="glass-card rounded-xl p-3">
      <MetaLine label={t('market.positioning')} info={t('market.positioning.info')} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-1.5">
        {shown.map((c) => {
          const total = c.long_value + c.short_value;
          const longPct = total > 0 ? (c.long_value / total) * 100 : 0;
          const top = p.top_assets.filter((a) => a.class === c.class).slice(0, 3);
          return (
            <div key={c.class} className="min-w-0">
              <div className="flex items-center gap-1.5 text-[10px]">
                <AssetClassBadge cls={c.class} />
                <span className={longPct >= 50 ? 'text-success' : 'text-danger'}>
                  {t('market.positioning.long', { pct: fmtFixed(longPct, 0) })}
                </span>
                <span className="flex-1" />
                <span className="font-mono text-muted-foreground">{compactUsd(total)}</span>
              </div>
              <div className="flex h-1.5 rounded overflow-hidden mt-1">
                <div className="bg-success" style={{ width: `${longPct}%` }} />
                <div className="bg-danger flex-1" />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1 truncate">
                {t('market.positioning.traders', { count: c.traders, n: c.traders.toLocaleString(intlLocale()) })}
                {top.length > 0 && ' · '}
                {top.map((a) => a.display).join(', ')}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Tile({
  label,
  meta,
  info,
  centred = false,
  children,
}: {
  label: string;
  meta?: SourceMeta;
  info?: string;
  /** Centre the body on both axes. For tiles whose content is one object --
   *  a reading, a ring -- rather than a column of lines. */
  centred?: boolean;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation('dashboard');
  return (
    <div className="glass-card rounded-xl p-3 min-h-[86px] flex flex-col">
      <MetaLine label={label} meta={meta} info={info} />
      <div className={`mt-1 flex-1 ${centred ? 'flex flex-col items-center justify-center' : ''}`}>
        {meta?.ok ? children : (
          <p className="text-xs text-muted-foreground mt-1">{t('market.unavailable')}</p>
        )}
      </div>
    </div>
  );
}

/**
 * The label, the attribution and the age, on one line.
 *
 * The age is shown ONLY when the value is stale. A fresh tile does not need to
 * explain itself, and a timestamp on every tile trains people to ignore all of
 * them — including the one that matters.
 */
function MetaLine({ label, meta, info }: { label: string; meta?: SourceMeta; info?: string }) {
  const { t } = useTranslation('dashboard');
  const tr = (k: string, o?: Record<string, unknown>) => String(t(k, o as any));
  return (
    <div className="flex items-center gap-1.5">
      <p className="text-[11px] text-muted-foreground truncate">{label}</p>
      {info && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-muted-foreground/60 cursor-help text-[10px]">ⓘ</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[260px] text-xs">{info}</TooltipContent>
        </Tooltip>
      )}
      <span className="flex-1" />
      {meta && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`text-[9px] shrink-0 cursor-help ${meta.stale ? 'text-warning' : 'text-muted-foreground/50'}`}>
              {meta.source}
            </span>
          </TooltipTrigger>
          <TooltipContent className="text-xs">
            {meta.ok
              ? meta.stale
                ? t('market.meta.stale', { source: meta.source, ago: ago(tr, meta.as_of) })
                : t('market.meta.fresh', { source: meta.source, ago: ago(tr, meta.as_of) })
              : t('market.meta.unreachable', { source: meta.source })}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * Dominance as a three-way ring: Bitcoin, Ethereum, everything else.
 *
 * A ring rather than one percentage, because "58% Bitcoin" leaves the reader to
 * work out what the rest is — and the rest is the part that moves when money
 * rotates. The three slices are the whole market by construction, so they sum
 * to 100 and the middle can state Bitcoin's share without ambiguity.
 */
function DominanceRing({ btc, eth }: { btc: number; eth: number }) {
  const { t } = useTranslation('dashboard');
  const others = Math.max(0, 100 - btc - eth);
  const C = 2 * Math.PI * 38;
  const slices = [
    { label: 'BTC', pct: btc, color: 'hsl(38, 92%, 55%)' },
    { label: 'ETH', pct: eth, color: 'hsl(230, 70%, 65%)' },
    { label: t('market.others'), pct: others, color: 'hsl(220, 10%, 45%)' },
  ];
  let offset = 0;

  return (
    <div className="flex items-center gap-2.5">
      <div className="relative w-[52px] h-[52px] shrink-0">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          {slices.map((sl) => {
            const dash = (sl.pct / 100) * C;
            const el = (
              <circle
                key={sl.label}
                cx="50" cy="50" r="38" fill="none"
                stroke={sl.color} strokeWidth="16"
                strokeDasharray={`${dash} ${C - dash}`}
                strokeDashoffset={-offset}
              />
            );
            offset += dash;
            return el;
          })}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[10px] font-bold">{fmtPct(btc, 0)}</span>
        </div>
      </div>
      <div className="min-w-0 space-y-0.5">
        {slices.map((sl) => (
          <div key={sl.label} className="flex items-center gap-1 text-[10px]">
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: sl.color }} />
            <span className="text-muted-foreground flex-1">{sl.label}</span>
            <span className="font-mono">{fmtPct(sl.pct, 1)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** One labelled two-sided bar. Used twice in the Pressure tile so the book and
 *  the liquidations are read the same way. */
function MiniSplit({
  label,
  leftPct,
  leftText,
  right,
}: {
  label: string;
  leftPct: number;
  leftText: string;
  right?: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
        <span className="w-[42px] shrink-0">{label}</span>
        <span className="flex-1 truncate">{leftText}</span>
        {right && <span className="font-mono shrink-0">{right}</span>}
      </div>
      <div className="flex h-1 rounded overflow-hidden mt-0.5 ms-[48px]">
        <div className="bg-success" style={{ width: `${Math.min(Math.max(leftPct, 0), 100)}%` }} />
        <div className="bg-danger flex-1" />
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline gap-1.5 text-[11px]">
      <span className="text-muted-foreground flex-1">{k}</span>
      <span className="font-mono">{v}</span>
    </div>
  );
}

function Bar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="h-1.5 rounded bg-white/[0.06] overflow-hidden mt-1.5">
      <div className="h-full rounded" style={{ width: `${Math.min(Math.max(pct, 0), 100)}%`, background: color }} />
    </div>
  );
}
